import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { useDragAndDrop } from '@codametrix/ui-components';
import { dragManager } from '@codametrix/ui-common';
import { useDragAutoScroll } from './use-drag-auto-scroll';
import { QueueCard } from '../../components/queue-card/QueueCard';
import {
  fetchQueues,
  savePriorityOrder,
  reorderQueues,
  revertOrder,
  QueueItem
} from '../../actions/queue-priority-order.action';
import { openModal } from '../../actions/manage-coders.action';
import { QueuePriorityOrderState } from '../../reducers/queue-priority-order.reducer';
import { ManageCodersModalContainer } from '../manage-coders/ManageCodersModalContainer';
import './QueuePriorityOrderView.scss';

const selectQueuePriorityOrder = (state: any): QueuePriorityOrderState =>
  state.queuePriorityOrder;

interface DragPreview {
  fromIndex: number;
  toIndex: number;
  /** Index in the currentOrder-based DOM list where the placeholder is inserted. */
  domInsertIndex: number;
  /** Maps queue id → 1-based priority number in the preview order. */
  priorityMap: Map<string, number>;
}

interface DraggableQueueCardProps {
  queue: QueueItem;
  index: number;
  priority: number;
  item: CMxCommonApp.DraggableSectionType;
  allItems: CMxCommonApp.DraggableSectionType[];
  dm: CMxCommonApp.DragManager;
  onDropHandler: (action: CMxCommonApp.DroppedAction<any>) => void;
  onDragOverCallback?: (action: CMxCommonApp.DroppedAction<any>) => void;
  onDragEndCallback?: () => void;
  onManageCoders?: () => void;
}

interface PlaceholderCardProps {
  onDrop: () => void;
}

const canDropOnto = () => true;

const PlaceholderCard: React.FC<PlaceholderCardProps> = ({ onDrop }) => (
  <div
    className="cmx-queue-priority-order-view__placeholder"
    onDragOver={ev => ev.preventDefault()}
    onDrop={ev => { ev.preventDefault(); ev.stopPropagation(); onDrop(); }}
    data-testid="drag-placeholder"
  />
);

const DraggableQueueCard: React.FC<DraggableQueueCardProps> = ({
  queue,
  index,
  priority,
  item,
  allItems,
  dm,
  onDropHandler,
  onDragOverCallback,
  onDragEndCallback,
  onManageCoders
}) => {
  const itemRef = React.useRef<HTMLDivElement>(null);

  const {
    isBeingDragged,
    dragHover,
    handleDragStart,
    onDragEnd,
    onDragLeave,
    onDragOver,
    onDrop
  } = useDragAndDrop({
    initialState: { isBeingDragged: false, dragHover: false, dragPosition: null },
    dragManager: dm,
    onDropHandler,
    onDragOverHandler: onDragOverCallback,
    onDragEndHandler: onDragEndCallback,
    canDropOnto,
    itemRef,
    item,
    allItems,
    dataKey: 'data',
    hitboxThreshold: 1 / 3
  });

  const className = [
    'cmx-queue-priority-order-view__item',
    dragHover ? 'cmx-queue-priority-order-view__item--drag-over' : '',
    isBeingDragged ? 'cmx-queue-priority-order-view__item--dragging' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={itemRef}
      className={className}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onDragLeave={onDragLeave}
      data-testid={`queue-item-${index}`}
    >
      <QueueCard
        priority={priority}
        queueName={queue.queueName}
        description={queue.description}
        status={queue.status}
        totalInQueue={queue.totalInQueue}
        criticalAged={queue.criticalAged}
        coderCount={queue.coderCount}
        onDragStart={handleDragStart}
        isDragging={isBeingDragged}
        onManageCoders={onManageCoders}
      />
    </div>
  );
};

export const QueuePriorityOrderView: React.FC = () => {
  const dispatch = useDispatch();
  const {
    queues,
    currentOrder,
    originalOrder,
    isLoading,
    isSaving,
    error,
    saveError
  } = useSelector(selectQueuePriorityOrder);
  const service = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );

  // Cards stay in currentOrder DOM positions throughout drag so sameObject checks
  // in useDragAndDrop remain correct. Only badge numbers and a ghost placeholder
  // move to reflect where the card will land.
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);

  const hasChanges = currentOrder.join(',') !== originalOrder.join(',');

  const dm = React.useMemo(() => dragManager.load('queue-priority-order'), []);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const isDragActive = React.useCallback(() => Boolean(dm.get()), [dm]);

  useDragAutoScroll(rootRef, isDragActive);

  useEffect(() => {
    if (service) {
      dispatch(fetchQueues({ service }) as any);
    }
  }, [dispatch, service]);

  const queueMap = React.useMemo(
    () => new Map(queues.map(q => [q.id, q])),
    [queues]
  );

  const orderedQueues = currentOrder
    .map(id => queueMap.get(id))
    .filter(Boolean) as QueueItem[];

  const draggableItems = React.useMemo(
    () =>
      orderedQueues.map(q => ({
        id: q.id,
        draggable: true,
        data: { id: q.id }
      })),
    [orderedQueues]
  );

  const clearPreview = React.useCallback(() => {
    setDragPreview(null);
  }, []);

  const handleDragOverPreview = React.useCallback(
    ({ dropCandidate, dropTarget, position }: CMxCommonApp.DroppedAction<any>) => {
      const fromIndex = currentOrder.indexOf(dropCandidate.id);
      const targetIndex = currentOrder.indexOf(dropTarget.id);
      const toIndex =
        position === 'before'
          ? fromIndex < targetIndex ? targetIndex - 1 : targetIndex
          : fromIndex < targetIndex ? targetIndex : targetIndex + 1;

      if (fromIndex === toIndex) {
        setDragPreview(null);
        return;
      }

      const newOrder = [...currentOrder];
      const [moved] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, moved);

      // domInsertIndex: where the placeholder appears in the fixed DOM order.
      // Dragging down → placeholder goes after the card now sitting at toIndex.
      // Dragging up   → placeholder goes before the card now sitting at toIndex.
      const domInsertIndex = fromIndex < toIndex ? toIndex + 1 : toIndex;

      // Auto-scroll makes dragover continuous, so an unchanged preview must not
      // re-render the whole list on every frame.
      setDragPreview(previous =>
        previous &&
        previous.fromIndex === fromIndex &&
        previous.toIndex === toIndex &&
        previous.domInsertIndex === domInsertIndex
          ? previous
          : {
              fromIndex,
              toIndex,
              domInsertIndex,
              priorityMap: new Map(newOrder.map((id, i) => [id, i + 1]))
            }
      );
    },
    [currentOrder]
  );

  const handlePlaceholderDrop = React.useCallback(() => {
    if (!dragPreview || dragPreview.fromIndex === dragPreview.toIndex) return;
    const { fromIndex, toIndex } = dragPreview;
    setDragPreview(null);
    dispatch(reorderQueues({ fromIndex, toIndex }) as any);
  }, [dragPreview, dispatch]);

  const handleDrop = React.useCallback(
    ({ dropCandidate, dropTarget, position }: CMxCommonApp.DroppedAction<any>) => {
      setDragPreview(null);
      const fromIndex = currentOrder.indexOf(dropCandidate.id);
      const targetIndex = currentOrder.indexOf(dropTarget.id);
      const toIndex =
        position === 'before'
          ? fromIndex < targetIndex ? targetIndex - 1 : targetIndex
          : fromIndex < targetIndex ? targetIndex : targetIndex + 1;
      if (fromIndex !== toIndex) {
        dispatch(reorderQueues({ fromIndex, toIndex }) as any);
      }
    },
    [currentOrder, dispatch]
  );

  const handleSave = React.useCallback(() => {
    if (service) {
      dispatch(savePriorityOrder({ service, orderedIds: currentOrder }) as any);
    }
  }, [currentOrder, dispatch, service]);

  const handleRevert = React.useCallback(() => {
    dispatch(revertOrder() as any);
  }, [dispatch]);

  const handleManageCoders = React.useCallback(
    (queue: QueueItem) => {
      dispatch(openModal({ queueId: queue.id, queueName: queue.queueName }) as any);
    },
    [dispatch]
  );

  if (isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="200px"
        data-testid="loading-spinner"
      >
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} data-testid="error-state">
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  const listItems: React.ReactNode[] = [];
  orderedQueues.forEach((queue, index) => {
    if (dragPreview?.domInsertIndex === index) {
      listItems.push(
        <PlaceholderCard key="drag-placeholder" onDrop={handlePlaceholderDrop} />
      );
    }
    listItems.push(
      <DraggableQueueCard
        key={queue.id}
        queue={queue}
        index={index}
        priority={dragPreview?.priorityMap.get(queue.id) ?? index + 1}
        item={draggableItems[index]}
        allItems={draggableItems}
        dm={dm}
        onDropHandler={handleDrop}
        onDragOverCallback={handleDragOverPreview}
        onDragEndCallback={clearPreview}
        onManageCoders={() => handleManageCoders(queue)}
      />
    );
  });
  if (dragPreview?.domInsertIndex === orderedQueues.length) {
    listItems.push(
      <PlaceholderCard key="drag-placeholder" onDrop={handlePlaceholderDrop} />
    );
  }

  return (
    <Box
      ref={rootRef}
      className="cmx-queue-priority-order-view"
      data-testid="queue-priority-order-view"
    >
      <Flex
        className="cmx-queue-priority-order-view__header"
        justify="space-between"
        align="center"
        mb={8}
      >
        <Box>
          <Heading size="md">Queue Priority Order</Heading>
          <Text
            className="cmx-queue-priority-order-view__subtitle"
            fontSize="sm"
            mt={1}
          >
            Drag to reorder. Cases from higher priority queues will be assigned
            to coders first.
          </Text>
        </Box>
        <Flex gap={6} align="center">
          {saveError && (
            <Text color="red.500" fontSize="sm" data-testid="save-error">
              {saveError}
            </Text>
          )}
          <Button
            onClick={handleRevert}
            isDisabled={!hasChanges || isSaving}
            variant="outline"
            data-testid="revert-priority-order-btn"
          >
            Revert
          </Button>
          <Button
            onClick={handleSave}
            isDisabled={!hasChanges || isSaving}
            isLoading={isSaving}
            colorScheme="blue"
            px={6}
            data-testid="save-priority-order-btn"
          >
            Save Priority Order
          </Button>
        </Flex>
      </Flex>

      <Box
        className="cmx-queue-priority-order-view__list"
        data-testid="queue-list"
      >
        {orderedQueues.length === 0 ? (
          <Text data-testid="empty-state">No queues available.</Text>
        ) : (
          listItems
        )}
      </Box>
      <ManageCodersModalContainer />
    </Box>
  );
};

QueuePriorityOrderView.displayName = 'QueuePriorityOrderView';
export default QueuePriorityOrderView;
